import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import adminAxios from '../../services/adminAxios';
import '../../assets/css/admin/moment-details.css';

const MomentDetailsPage = () => {
  const { momentId } = useParams();
  const navigate = useNavigate();
  const [moment, setMoment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchMomentDetails();
  }, [momentId]);

  const fetchMomentDetails = async () => {
    try {
      setLoading(true);
      const response = await adminAxios.get(`/api/admin/moments/${momentId}`);
      
      if (response.data.success) {
        setMoment(response.data.moment);
        setError(null);
      } else {
        setError('Failed to load moment details');
      }
    } catch (err) {
      console.error('Error fetching moment details:', err);
      setError('Error loading moment details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMoment = async () => {
    if (!window.confirm('Are you sure you want to delete this moment? This action cannot be undone.')) {
      return;
    }
    
    try {
      setLoading(true);
      const response = await adminAxios.delete(`/api/admin/moments/${momentId}`);
      
      if (response.data.success) {
        // Navigate back to the event page if we have an event ID
        if (moment?.event_id) {
          navigate(`/admin/events/${moment.event_id}`);
        } else {
          navigate('/admin/dashboard');
        }
      } else {
        setError(response.data.message || 'Failed to delete moment');
      }
    } catch (err) {
      console.error('Error deleting moment:', err);
      setError('Error deleting moment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-loading-container">
          <div className="spinner"></div>
          <p>Loading moment details...</p>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="alert alert-danger">{error}</div>
      </AdminLayout>
    );
  }

  if (!moment) {
    return (
      <AdminLayout>
        <div className="moment-not-found">
          <h2>Moment Not Found</h2>
          <p>The moment you're looking for doesn't exist or has been removed.</p>
          <div className="mt-4">
            <Link to="/admin/dashboard" className="btn btn-primary">
              <i className="fa fa-home me-2"></i> Back to Dashboard
            </Link>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="moment-details-page">
        <div className="back-link mb-4">
          {moment.event_id ? (
            <Link to={`/admin/events/${moment.event_id}`}>
              <i className="fa fa-arrow-left"></i> Back to Event
            </Link>
          ) : (
            <Link to="/admin/dashboard">
              <i className="fa fa-arrow-left"></i> Back to Dashboard
            </Link>
          )}
        </div>

        <div className="moment-details-container">
          <div className="moment-preview-section">
            <div className="moment-preview-card">
              <h3>Moment Preview</h3>
              <div className="moment-content">
                {moment.mime_type && moment.mime_type.startsWith('image/') ? (
                  <img 
                    src={moment.url} 
                    alt={moment.footage_filename} 
                    className="moment-image" 
                  />
                ) : moment.mime_type && moment.mime_type.startsWith('video/') ? (
                  <video 
                    controls 
                    className="moment-video"
                  >
                    <source src={moment.url} type={moment.mime_type} />
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <div className="file-preview">
                    <i className="fa fa-file-o file-icon"></i>
                    <p>File preview not available</p>
                  </div>
                )}
              </div>
              <div className="moment-actions">
                <a 
                  href={moment.url} 
                  download={moment.footage_filename}
                  className="btn btn-outline-primary"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fa fa-download me-2"></i> Download
                </a>
                <button 
                  className="btn btn-outline-danger"
                  onClick={handleDeleteMoment}
                >
                  <i className="fa fa-trash me-2"></i> Delete
                </button>
              </div>
            </div>
          </div>

          <div className="moment-info-section">
            <div className="moment-info-card">
              <h3>Moment Details</h3>
              
              <div className="info-group">
                <div className="info-label">Filename</div>
                <div className="info-value">{moment.footage_filename}</div>
              </div>
              
              <div className="info-group">
                <div className="info-label">Event</div>
                <div className="info-value">
                  {moment.event_title ? (
                    <Link to={`/admin/events/${moment.event_id}`}>
                      {moment.event_title}
                    </Link>
                  ) : 'Unknown Event'}
                </div>
              </div>
              
              <div className="info-group">
                <div className="info-label">Uploaded By</div>
                <div className="info-value">
                  {moment.user_name ? (
                    <Link to={`/admin/users/${moment.user_id}`}>
                      {moment.user_name}
                    </Link>
                  ) : 'Unknown User'}
                </div>
              </div>
              
              <div className="info-group">
                <div className="info-label">Upload Date</div>
                <div className="info-value">
                  {new Date(moment.uploaded_at).toLocaleString()}
                </div>
              </div>
              
              <div className="info-group">
                <div className="info-label">File Type</div>
                <div className="info-value">{moment.mime_type || 'Unknown'}</div>
              </div>
              
              <div className="info-group">
                <div className="info-label">File Size</div>
                <div className="info-value">
                  {moment.file_size ? `${Math.round(moment.file_size / 1024)} KB` : 'N/A'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default MomentDetailsPage;
